import { useId, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { cx } from './cx';
import type { MoneyString } from '../lib/types';
import './components.css';

/* ---------------------------------------------------------------- button */

export interface ButtonProps {
  children?: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean;
  block?: boolean;
  /** Leading glyph. Keep it to one character or a small inline SVG. */
  icon?: ReactNode;
  title?: string;
  ariaLabel?: string;
  className?: string;
}

export function Button({
  children, onClick, variant = 'default', size = 'md', type = 'button',
  disabled, loading, block, icon, title, ariaLabel, className,
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx('k-btn', variant !== 'default' && `k-btn--${variant}`,
        size !== 'md' && `k-btn--${size}`, block && 'k-btn--block', className)}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="k-btn__spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps {
  children: ReactNode;
  onClick?: () => void;
  /** Required — an icon with no label is invisible to a screen reader. */
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Press-and-hold handlers, on the BUTTON itself.
   *
   * They belong here rather than on a wrapper inside the button: a `<span>`
   * carrying the pointer events is not focusable, so the control ends up
   * working for a mouse and dead to a keyboard. `onClick` still fires for a
   * keyboard activation, which is what makes both paths work.
   */
  onPressStart?: () => void;
  onPressEnd?: () => void;
  onPressCancel?: () => void;
}

export function IconButton({
  children, onClick, label, pressed, disabled, className,
  onPressStart, onPressEnd, onPressCancel,
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={cx('k-icon-btn', className)}
      onClick={onClick}
      onPointerDown={onPressStart}
      onPointerUp={onPressEnd}
      onPointerLeave={onPressCancel}
      onPointerCancel={onPressCancel}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
    >
      {children}
    </button>
  );
}

/* ----------------------------------------------------------------- field */

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  htmlFor?: string;
  className?: string;
}

/** Label + control + hint/error. Every input on every form uses one. */
export function Field({ label, children, hint, error, required, htmlFor, className }: FieldProps) {
  return (
    <div className={cx('k-field', className)}>
      <label className="k-field__label" htmlFor={htmlFor}>
        {label}{required && <span className="k-field__req" aria-hidden="true"> *</span>}
      </label>
      {children}
      {error ? <span className="k-field__error" role="alert">{error}</span>
             : hint ? <span className="k-field__hint">{hint}</span> : null}
    </div>
  );
}

/* --------------------------------------------------------------- inputs */

export interface TextInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'search' | 'email' | 'date' | 'time' | 'number';
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  /** iOS keyboard hint. */
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'search';
}

export function TextInput({
  value, onChange, placeholder, type = 'text', id, invalid, disabled, ariaLabel, className, inputMode,
}: TextInputProps) {
  return (
    <input
      id={id}
      className={cx('k-input', invalid && 'k-input--invalid', className)}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      inputMode={inputMode}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  );
}

export interface MoneyInputProps {
  /** `null` means "not entered" — which is not zero, and is emitted as null. */
  value: MoneyString | null;
  onChange: (v: MoneyString | null) => void;
  placeholder?: string;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Allow negative figures — over/short, adjustments. */
  allowNegative?: boolean;
  className?: string;
}

/**
 * Money entry that keeps the exact digits typed. The value never becomes a
 * number, and an empty box emits `null` rather than `"0.00"` — an unentered
 * count and a counted zero are different facts.
 */
export function MoneyInput({
  value, onChange, placeholder = '0.00', id, invalid, disabled, ariaLabel, allowNegative, className,
}: MoneyInputProps) {
  const [raw, setRaw] = useState<string | null>(null);
  const shown = raw ?? (value ?? '');

  const handle = (next: string) => {
    const pattern = allowNegative ? /[^0-9.-]/g : /[^0-9.]/g;
    let clean = next.replace(pattern, '');
    // One decimal point, at most two decimals, one leading minus.
    const neg = allowNegative && clean.startsWith('-');
    clean = clean.replace(/-/g, '');
    const [i, ...rest] = clean.split('.');
    clean = rest.length ? `${i}.${rest.join('').slice(0, 2)}` : i;
    const final = `${neg ? '-' : ''}${clean}`;
    setRaw(final);
    onChange(final === '' || final === '-' ? null : final);
  };

  return (
    <span className={cx('k-input-wrap', 'k-input-wrap--money', className)}>
      <span className="k-input-wrap__prefix" aria-hidden="true">$</span>
      <input
        id={id}
        className={cx('k-input', 'k-input--money', invalid && 'k-input--invalid')}
        type="text"
        inputMode="decimal"
        value={shown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        onChange={(e) => handle(e.target.value)}
        onBlur={() => setRaw(null)}
      />
    </span>
  );
}

export interface SelectProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  id?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function Select<T extends string>({
  value, onChange, options, id, disabled, ariaLabel, className,
}: SelectProps<T>) {
  return (
    <select
      id={id}
      className={cx('k-input', className)}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
      ))}
    </select>
  );
}

export interface TextareaProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  rows?: number;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function Textarea({ value, onChange, placeholder, id, rows = 3, disabled, ariaLabel, className }: TextareaProps) {
  return (
    <textarea
      id={id}
      className={cx('k-input', className)}
      value={value}
      rows={rows}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Convenience: a field id that is stable across renders. */
export function useFieldId(prefix = 'f'): string {
  const id = useId();
  return `${prefix}${id}`;
}
